import WishLegacyPriority from 'app/frontend/types/custom/WishLegacyPriority'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  Link as LinkIcon,
} from 'lucide-react'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import ItemIcon from 'app/frontend/base/components/ItemIcon'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import { PropsWithChildren, useContext } from 'react'
import { LootContext } from '../context'
import {
  TeamWishlistItem,
  TeamWishlistItemCharacterWishlist,
} from 'app/frontend/types/custom/TeamWishlistEncounter'
import WishlistAlternativeTooltipContent from 'app/frontend/base/components/WishlistAlternativeTooltipContent'
import WishLegacyPriorityDot from 'app/frontend/base/components/WishLegacyPriorityDot'

const baseWowheadUrl = `https://wowhead.com/`

type Props = PropsWithChildren<{
  wishlist: TeamWishlistItemCharacterWishlist
  itemInfo?: TeamWishlistItem
}>

const TeamWishTooltip = ({ children, wishlist, itemInfo }: Props) => {
  const { valueDisplay } = useContext(LootContext)

  return (
    <HoverCard openDelay={50} closeDelay={50}>
      <HoverCardTrigger>{children}</HoverCardTrigger>
      <HoverCardContent className='text-xs w-fit'>
        {itemInfo ? (
          <a
            target='_blank'
            className={`inline-flex items-center gap-1.5 text-quality-${itemInfo.quality} text-[1rem] mb-3`}
            rel='noreferrer'
            href={`${baseWowheadUrl}item=${itemInfo.id}?ilvl=${
              itemInfo.item_level
            }${
              itemInfo.bonus_ids.length
                ? `&bonus=${itemInfo.bonus_ids.join(':')}`
                : ''
            }`}
          >
            <ItemIcon id={itemInfo.id} className='w-4 h-4' />

            {itemInfo.name}
          </a>
        ) : null}

        {wishlist.alternative ? (
          <div className='flex items-start gap-2 mb-2 max-w-60'>
            <AlertTriangle
              size={14}
              className='text-warning-foreground shrink-0'
            />
            <WishlistAlternativeTooltipContent
              alternative={wishlist.alternative}
              valueDisplay={valueDisplay}
            />
          </div>
        ) : !wishlist.wishes.some((wish) => wish.manually_edited) ? (
          <div className='flex items-start gap-2 mb-2 max-w-60'>
            <CheckCircle
              size={14}
              className='text-success-foreground shrink-0'
            />
            <div className='text-xs'>
              This is the best in slot item for this character on this
              difficulty.
            </div>
          </div>
        ) : (
          ''
        )}

        {wishlist.edited ? (
          <div className='flex items-start gap-2 mb-2 max-w-60'>
            <AlertTriangle
              size={14}
              className='text-warning-foreground shrink-0'
            />
            <div className='text-xs'>This is a manually edited wish.</div>
          </div>
        ) : wishlist.outdated ? (
          <div className='flex items-start gap-2 mb-2 max-w-60'>
            <AlertTriangle
              size={14}
              className='text-warning-foreground shrink-0'
            />
            <div className='text-xs'>
              This wish is outdated, the{' '}
              <a
                target='_blank'
                rel='noreferrer'
                className='text-primary hover:text-primary/90'
                href={`${baseWowheadUrl}item=${wishlist.outdated.old.id}?ilvl=${wishlist.outdated.old.item_level}`}
              >
                item
              </a>{' '}
              worn when running the Droptimizer report doesn&apos;t match the{' '}
              <a
                target='_blank'
                rel='noreferrer'
                className='text-primary hover:text-primary/90'
                href={`${baseWowheadUrl}item=${wishlist.outdated.new.id}?ilvl=${wishlist.outdated.new.item_level}`}
              >
                item
              </a>{' '}
              that is currently worn.
            </div>
          </div>
        ) : (
          ''
        )}

        <table className='whitespace-nowrap [&_td]:px-1 [&_th]:px-1 text-left'>
          <thead>
            <tr>
              <th />
              <th>Style</th>
              <th>Weight</th>
              <th>Upgrade</th>
              <th className='flex items-end'>
                <Tooltip>
                  <TooltipTrigger>
                    <Clock size={14} />
                  </TooltipTrigger>
                  <TooltipContent>Age in days</TooltipContent>
                </Tooltip>
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {wishlist.wishes.map((wish, i) => {
              return (
                <tr key={i}>
                  <td>
                    <BlizzardIcon
                      iconName={wish.specialization_icon}
                      size='small'
                      className='min-h-[16px] min-w-[16px]'
                    />
                  </td>
                  <td>{wish.fight_style}</td>
                  <td className='opacity-60'>{wish.weight_percentage}%</td>
                  <td>
                    {wish.wish_type === 'raidbots' ||
                    wish.wish_type === 'percentage' ||
                    wish.wish_type === 'qe' ? (
                      valueDisplay === 'percentage' ? (
                        `${wish.percentage?.toLocaleString('en-US', {
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                        })}%`
                      ) : (
                        `${
                          wish.absolute?.toLocaleString('en-US', {
                            maximumFractionDigits: 0,
                          }) || 0
                        }`
                      )
                    ) : (
                      <WishLegacyPriorityDot
                        priority={
                          (wish.upgrade_level as WishLegacyPriority) ?? 'none'
                        }
                      />
                    )}
                  </td>
                  <td
                    className={`flex justify-center ${
                      wish.age && wish.age > 21
                        ? 'text-destructive-foreground'
                        : wish.age && wish.age > 7
                        ? 'text-warning-foreground'
                        : 'text-success-foreground'
                    }`}
                  >
                    {wish.age}
                  </td>
                  <td>
                    {wish.report_url && (
                      <a
                        href={wish.report_url}
                        target='_blank'
                        rel='noreferrer'
                        title='Raidbots'
                      >
                        <LinkIcon size={14} />
                      </a>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </HoverCardContent>
    </HoverCard>
  )
}

export default TeamWishTooltip
